import { useRef, useState } from 'react';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import ButtonBase from '@mui/material/ButtonBase';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import Transitions from 'components/@extended/Transitions';
import { useWorkspace } from 'contexts/WorkspaceContext';

// assets
import DownOutlined from '@ant-design/icons/DownOutlined';

// ==============================|| HEADER CONTENT - WORKSPACE ||============================== //

export default function Workspace() {
  const downSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const { communities, includeAll, loading, selected, selectCommunity, activeCommunityId } = useWorkspace();

  const anchorRef = useRef<any>(null);
  const [open, setOpen] = useState(false);

  const handleToggle = () => setOpen((p) => !p);
  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  const displayName = selected?.name || (loading ? 'Loading…' : includeAll ? 'All Communities' : communities[0]?.name || 'Select');

  // Build dropdown options, exclude current selection.
  const menuOptions = [
    ...(includeAll ? [{ id: 'all', name: 'All Communities' }] : []),
    ...communities.map((c: any) => ({
      id: String(c?.documentId ?? c?.id ?? ''),
      name: c?.name ?? c?.title ?? `Community ${c?.id ?? ''}`,
      image: (c as any)?.image ?? null,
      label: (c as any)?.label
    }))
  ].filter((opt) => (activeCommunityId === '' ? opt.id !== 'all' : opt.id !== activeCommunityId));

  const hasMultipleOptions = menuOptions.length > 1;

  return (
    <Box>
      <ButtonBase
        disableRipple
        ref={anchorRef}
        tabIndex={0}
        onClick={hasMultipleOptions ? handleToggle : undefined}
        sx={{
          height: 38,
          minWidth: { xs: 60, sm: 200 },
          border: '0',
          borderColor: 'divider',
          px: 1,
          py: 0.5,
          borderRadius: 1,
          justifyContent: 'flex-start',
          gap: 0.75,
          cursor: hasMultipleOptions ? 'pointer' : 'default',
          '&:focus-visible': { outline: `2px solid`, outlineColor: 'secondary.dark' }
        }}
      >
        {/* Avatar optional */}
        {/* <Avatar
          alt={displayName}
          variant="rounded"
          src={workspace?.image ? getImageUrl(workspace.image, ImagePath.WORKSPACE) : undefined}
          sx={{ width: 24, height: 24 }}
        /> */}

        {!downSM && (
          <>
            <Box component="span" sx={{ typography: 'h6', whiteSpace: 'nowrap' }}>
              {displayName}
            </Box>
          </>
        )}
        {hasMultipleOptions && (
          <Box sx={{ color: 'text.secondary', ml: 'auto' }}>
            <DownOutlined style={{ fontSize: 10 }} />
          </Box>
        )}
      </ButtonBase>

      {hasMultipleOptions && (
        <Popper
          placement={downMD ? 'bottom-start' : 'bottom'}
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
          popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? 0 : 0, 9] } }] }}
        >
          {({ TransitionProps }) => (
            <Transitions type="grow" position={downMD ? 'top-left' : 'top'} in={open} {...TransitionProps}>
              <Paper sx={(theme) => ({ boxShadow: theme.vars.customShadows.z1, width: 220 })}>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList>
                    {menuOptions.map((item) => (
                      <MenuItem
                        key={item.id}
                        onClick={async () => {
                          await selectCommunity(item.id === 'all' ? 'all' : item.id);
                          setOpen(false);
                        }}
                        sx={{ gap: 1, px: 1 }}
                      >
                        <Typography noWrap component="span" variant="caption">
                          {item.name}
                        </Typography>
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Transitions>
          )}
        </Popper>
      )}
    </Box>
  );
}
